// MVP Profile Page - No Bullshit, Lean, Purpose-Driven
// Single-page profile with: identity, Entry Quiz, socials, preferences, delete account
// View mode by default, Edit mode on button click

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { ArrowLeft, User, Target, Globe, Settings, Trash2, Save, Loader2, Edit, X, LogOut } from "lucide-react";
import { ProfileImageUpload } from "@/components/profile/ProfileImageUpload";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { fetchProfile, updateProfile } from "@/services/profile-service";
import { BottomTabs } from "../navigation/BottomTabs";
import { useMobilityFlag } from "@/hooks/useMobilityFlag";
import { MobilityProfileTab } from "@/features/mobility/components/MobilityProfileTab";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronRight, Activity } from "lucide-react";

export default function ProfileScreen() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { toast } = useToast();

  // Profile state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { enabled: mobilityEnabled } = useMobilityFlag();
  const [mobilityExpanded, setMobilityExpanded] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    // Personal Info
    first_name: "",
    last_name: "",
    nickname: "",
    mobile: "",
    age: "",
    address: "",
    // Entry Quiz
    handicap: "",
    primary_miss: "",
    secondary_miss: "",
    practice_frequency: "",
    goals: "",
    pain_points: "",
    learning_style: "",
    // Socials
    instagram_handle: "",
    tiktok_handle: "",
    facebook_handle: "",
    x_handle: "",
    // Preferences
    notifications_enabled: true,
    video_quality: "high",
    dominant_hand: "",
  });

  // Load profile on mount
  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const data = await fetchProfile(user.id);
      if (data) {
        setProfile(data);
        setFormData({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          nickname: data.nickname || "",
          mobile: data.mobile || "",
          age: data.age?.toString() || "",
          address: data.address || "",
          handicap: data.handicap?.toString() || "",
          primary_miss: data.primary_miss || "",
          secondary_miss: data.secondary_miss || "",
          practice_frequency: data.practice_frequency || "",
          goals: data.goals || "",
          pain_points: data.pain_points || "",
          learning_style: data.learning_style || "",
          instagram_handle: data.instagram_handle || "",
          tiktok_handle: data.tiktok_handle || "",
          facebook_handle: data.facebook_handle || "",
          x_handle: data.x_handle || "",
          notifications_enabled: data.notifications_enabled ?? true,
          video_quality: data.video_quality || "high",
          dominant_hand: data.dominant_hand || "",
        });
      }
    } catch (error) {
      console.error("[ProfileScreen] Error loading profile:", error);
      toast({
        title: "Error loading profile",
        description: "Failed to load your profile data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const updates: any = {
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        nickname: formData.nickname || null,
        mobile: formData.mobile || null,
        age: formData.age ? parseInt(formData.age) : null,
        address: formData.address || null,
        handicap: formData.handicap ? parseInt(formData.handicap) : null,
        primary_miss: formData.primary_miss || null,
        secondary_miss: formData.secondary_miss || null,
        practice_frequency: formData.practice_frequency || null,
        goals: formData.goals || null,
        pain_points: formData.pain_points || null,
        learning_style: formData.learning_style || null,
        instagram_handle: formData.instagram_handle || null,
        tiktok_handle: formData.tiktok_handle || null,
        facebook_handle: formData.facebook_handle || null,
        x_handle: formData.x_handle || null,
        notifications_enabled: formData.notifications_enabled,
        video_quality: formData.video_quality,
        dominant_hand: formData.dominant_hand || null,
      };

      const result = await updateProfile(user.id, updates);
      if (result.success) {
        toast({
          title: "Profile updated",
          description: "Your profile has been saved successfully",
        });
        // Reload profile to get updated data
        await loadProfile();
        setIsEditing(false); // Exit edit mode
      } else {
        toast({
          title: "Save failed",
          description: result.error || "Failed to save profile",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("[ProfileScreen] Error saving profile:", error);
      toast({
        title: "Save failed",
        description: error?.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reload profile data to reset form
    if (user) {
      loadProfile();
    }
    setIsEditing(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
      });
      navigate("/mvp/home");
    } catch (error) {
      toast({
        title: "Error signing out",
        description: "There was an error signing out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAccount = async () => {
    // Note: Actual account deletion should be handled server-side
    // For MVP, we'll just sign out and show a message
    toast({
      title: "Account deletion",
      description: "Account deletion must be handled through support. Please contact us.",
    });
    setDeleteDialogOpen(false);
  };

  const getInitials = () => {
    const first = formData.first_name?.charAt(0) || "";
    const last = formData.last_name?.charAt(0) || "";
    if (first || last) return `${first}${last}`.toUpperCase();
    return user?.email?.charAt(0).toUpperCase() || "G";
  };

  const displayName = formData.nickname || formData.first_name || user?.email?.split("@")[0] || "Golfer";

  // Helper to render field value or placeholder
  const renderFieldValue = (value: string, placeholder: string = "Not set") => {
    return value || <span className="text-gray-400 italic">{placeholder}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-24 bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#196644] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] px-6 py-4 flex items-center gap-4 h-20 shadow-sm">
        <button
          onClick={() => navigate("/mvp/home")}
          className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <div className="flex-1 flex justify-center">
          <img 
            src="/nodoubles_logo_trans.png" 
            alt="No Doubles Golf Co" 
            className="h-12 object-contain"
          />
        </div>
        <div className="w-10" />
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        {/* Profile Header Section */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-[0_2px_8px_rgba(13,56,42,0.08)]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3 sm:gap-4 flex-1 w-full sm:w-auto">
              {isEditing ? (
                <ProfileImageUpload
                  type="profile"
                  currentImageUrl={profile?.profile_image_url}
                  size="lg"
                  onUploadComplete={(url) => {
                    setProfile({ ...profile, profile_image_url: url });
                  }}
                />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] flex items-center justify-center text-[#0D382A] text-2xl sm:text-3xl overflow-hidden flex-shrink-0">
                  {profile?.profile_image_url ? (
                    <img
                      src={profile.profile_image_url}
                      alt={displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getInitials()
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-[#0D382A] text-xl sm:text-2xl mb-1 truncate">{displayName}</h2>
                <p className="text-gray-600 text-xs sm:text-sm truncate">{user?.email}</p>
                {formData.nickname && (
                  <p className="text-gray-500 text-xs mt-1 truncate">Display name: {formData.nickname}</p>
                )}
              </div>
            </div>
            {!isEditing && (
              <Button
                onClick={() => setIsEditing(true)}
                className="bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] hover:shadow-lg w-full sm:w-auto"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Personal Info Section */}
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(13,56,42,0.08)] overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-[#196644]" />
              <h3 className="text-[#0D382A] text-xl">Personal Info</h3>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {isEditing ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name" className="text-sm text-gray-600">
                      First Name *
                    </Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-sm text-gray-600">
                      Last Name *
                    </Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="nickname" className="text-sm text-gray-600">
                    Nickname (your display name) *
                  </Label>
                  <Input
                    id="nickname"
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                    className="mt-1"
                    placeholder="How you want to be displayed"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mobile" className="text-sm text-gray-600">
                      Mobile (optional)
                    </Label>
                    <Input
                      id="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="age" className="text-sm text-gray-600">
                      Age (optional)
                    </Label>
                    <Input
                      id="age"
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="text-sm text-gray-600">
                    Address (optional)
                  </Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-4 divide-y divide-gray-100">
                <div className="pt-2">
                  <p className="text-gray-500 text-sm mb-1">Name</p>
                  <p className="text-[#0D382A]">
                    {formData.first_name || formData.last_name
                      ? `${formData.first_name || ""} ${formData.last_name || ""}`.trim()
                      : renderFieldValue("", "Not set")}
                  </p>
                </div>
                <div className="pt-4">
                  <p className="text-gray-500 text-sm mb-1">Nickname</p>
                  <p className="text-[#0D382A]">{renderFieldValue(formData.nickname)}</p>
                </div>
                {formData.mobile && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Mobile</p>
                    <p className="text-[#0D382A]">{formData.mobile}</p>
                  </div>
                )}
                {formData.age && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Age</p>
                    <p className="text-[#0D382A]">{formData.age}</p>
                  </div>
                )}
                {formData.address && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Address</p>
                    <p className="text-[#0D382A]">{formData.address}</p>
                  </div>
                )}
                <div className="pt-4">
                  <p className="text-gray-500 text-sm mb-1">Email</p>
                  <p className="text-[#0D382A]">{user?.email}</p>
                </div>
              </div>
            )}

            {isEditing && (
              <div>
                <Label className="text-sm text-gray-600">Email</Label>
                <Input value={user?.email || ""} disabled className="mt-1 bg-gray-50" />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed here</p>
              </div>
            )}
          </div>
        </div>

        {/* Swing Profile (Entry Quiz) Section */}
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(13,56,42,0.08)] overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-[#196644]" />
              <h3 className="text-[#0D382A] text-xl">Your Swing Profile</h3>
            </div>
            {isEditing && (
              <p className="text-sm text-gray-500 mt-1">Update your Entry Quiz answers (editable for adaptive drills)</p>
            )}
          </div>

          <div className="p-6 space-y-4">
            {isEditing ? (
              <>
                <div>
                  <Label htmlFor="handicap" className="text-sm text-gray-600">
                    Handicap Level
                  </Label>
                  <Input
                    id="handicap"
                    type="number"
                    value={formData.handicap}
                    onChange={(e) => setFormData({ ...formData, handicap: e.target.value })}
                    className="mt-1"
                    placeholder="e.g., 15"
                  />
                </div>

                <div>
                  <Label htmlFor="primary_miss" className="text-sm text-gray-600">
                    Primary Miss
                  </Label>
                  <Input
                    id="primary_miss"
                    value={formData.primary_miss}
                    onChange={(e) => setFormData({ ...formData, primary_miss: e.target.value })}
                    className="mt-1"
                    placeholder="e.g., slice, hook, fat, thin"
                  />
                </div>

                <div>
                  <Label htmlFor="secondary_miss" className="text-sm text-gray-600">
                    Secondary Miss
                  </Label>
                  <Input
                    id="secondary_miss"
                    value={formData.secondary_miss}
                    onChange={(e) => setFormData({ ...formData, secondary_miss: e.target.value })}
                    className="mt-1"
                    placeholder="e.g., slice, hook, fat, thin"
                  />
                </div>

                <div>
                  <Label htmlFor="practice_frequency" className="text-sm text-gray-600">
                    Practice Frequency
                  </Label>
                  <Select
                    value={formData.practice_frequency}
                    onValueChange={(value) => setFormData({ ...formData, practice_frequency: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="rarely">Rarely</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="goals" className="text-sm text-gray-600">
                    Goals
                  </Label>
                  <Textarea
                    id="goals"
                    value={formData.goals}
                    onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
                    className="mt-1"
                    placeholder="What are your golf improvement goals?"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="pain_points" className="text-sm text-gray-600">
                    Pain Points
                  </Label>
                  <Textarea
                    id="pain_points"
                    value={formData.pain_points}
                    onChange={(e) => setFormData({ ...formData, pain_points: e.target.value })}
                    className="mt-1"
                    placeholder="What frustrates you most about your game?"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="learning_style" className="text-sm text-gray-600">
                    Learning Style
                  </Label>
                  <Select
                    value={formData.learning_style}
                    onValueChange={(value) => setFormData({ ...formData, learning_style: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select learning style" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visual">Visual</SelectItem>
                      <SelectItem value="kinesthetic">Kinesthetic</SelectItem>
                      <SelectItem value="analytical">Analytical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div className="space-y-4 divide-y divide-gray-100">
                {formData.handicap && (
                  <div className="pt-2">
                    <p className="text-gray-500 text-sm mb-1">Handicap</p>
                    <p className="text-[#0D382A]">{formData.handicap}</p>
                  </div>
                )}
                {formData.primary_miss && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Primary Miss</p>
                    <p className="text-[#0D382A]">{formData.primary_miss}</p>
                  </div>
                )}
                {formData.secondary_miss && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Secondary Miss</p>
                    <p className="text-[#0D382A]">{formData.secondary_miss}</p>
                  </div>
                )}
                {formData.practice_frequency && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Practice Frequency</p>
                    <p className="text-[#0D382A] capitalize">{formData.practice_frequency}</p>
                  </div>
                )}
                {formData.goals && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Goals</p>
                    <p className="text-[#0D382A]">{formData.goals}</p>
                  </div>
                )}
                {formData.pain_points && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Pain Points</p>
                    <p className="text-[#0D382A]">{formData.pain_points}</p>
                  </div>
                )}
                {formData.learning_style && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Learning Style</p>
                    <p className="text-[#0D382A] capitalize">{formData.learning_style}</p>
                  </div>
                )}
                {!formData.handicap && !formData.primary_miss && !formData.secondary_miss && !formData.practice_frequency && !formData.goals && !formData.pain_points && !formData.learning_style && (
                  <div className="pt-2">
                    <p className="text-gray-400 italic">No swing profile data yet. Click Edit to add your information.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Social Accounts Section */}
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(13,56,42,0.08)] overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-[#196644]" />
              <h3 className="text-[#0D382A] text-xl">Social Accounts (optional)</h3>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {isEditing ? (
              <>
                <div>
                  <Label htmlFor="instagram_handle" className="text-sm text-gray-600">
                    Instagram
                  </Label>
                  <Input
                    id="instagram_handle"
                    value={formData.instagram_handle}
                    onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value })}
                    className="mt-1"
                    placeholder="@username"
                  />
                </div>

                <div>
                  <Label htmlFor="tiktok_handle" className="text-sm text-gray-600">
                    TikTok
                  </Label>
                  <Input
                    id="tiktok_handle"
                    value={formData.tiktok_handle}
                    onChange={(e) => setFormData({ ...formData, tiktok_handle: e.target.value })}
                    className="mt-1"
                    placeholder="@username"
                  />
                </div>

                <div>
                  <Label htmlFor="facebook_handle" className="text-sm text-gray-600">
                    Facebook
                  </Label>
                  <Input
                    id="facebook_handle"
                    value={formData.facebook_handle}
                    onChange={(e) => setFormData({ ...formData, facebook_handle: e.target.value })}
                    className="mt-1"
                    placeholder="Username or URL"
                  />
                </div>

                <div>
                  <Label htmlFor="x_handle" className="text-sm text-gray-600">
                    X (Twitter)
                  </Label>
                  <Input
                    id="x_handle"
                    value={formData.x_handle}
                    onChange={(e) => setFormData({ ...formData, x_handle: e.target.value })}
                    className="mt-1"
                    placeholder="@username"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-4 divide-y divide-gray-100">
                {formData.instagram_handle && (
                  <div className="pt-2">
                    <p className="text-gray-500 text-sm mb-1">Instagram</p>
                    <p className="text-[#0D382A]">{formData.instagram_handle}</p>
                  </div>
                )}
                {formData.tiktok_handle && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">TikTok</p>
                    <p className="text-[#0D382A]">{formData.tiktok_handle}</p>
                  </div>
                )}
                {formData.facebook_handle && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">Facebook</p>
                    <p className="text-[#0D382A]">{formData.facebook_handle}</p>
                  </div>
                )}
                {formData.x_handle && (
                  <div className="pt-4">
                    <p className="text-gray-500 text-sm mb-1">X (Twitter)</p>
                    <p className="text-[#0D382A]">{formData.x_handle}</p>
                  </div>
                )}
                {!formData.instagram_handle && !formData.tiktok_handle && !formData.facebook_handle && !formData.x_handle && (
                  <div className="pt-2">
                    <p className="text-gray-400 italic">No social accounts added. Click Edit to add them.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobility Section - Only show when feature flag is enabled */}
        {mobilityEnabled && (
          <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(13,56,42,0.08)] overflow-hidden">
            <Collapsible open={mobilityExpanded} onOpenChange={setMobilityExpanded}>
              <CollapsibleTrigger className="w-full">
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Activity className="w-5 h-5 text-[#196644]" />
                      <h3 className="text-[#0D382A] text-xl">Mobility</h3>
                    </div>
                    <ChevronRight 
                      className={`w-5 h-5 text-gray-400 transition-transform ${mobilityExpanded ? 'rotate-90' : ''}`} 
                    />
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-6">
                  <MobilityProfileTab />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Preferences Section */}
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(13,56,42,0.08)] overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-[#196644]" />
              <h3 className="text-[#0D382A] text-xl">Preferences</h3>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="notifications" className="text-sm text-[#0D382A]">
                  Notifications
                </Label>
                <p className="text-xs text-gray-500 mt-1">Daily reminders and updates</p>
              </div>
              {isEditing ? (
                <Switch
                  id="notifications"
                  checked={formData.notifications_enabled}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, notifications_enabled: checked })
                  }
                />
              ) : (
                <span className={`text-sm ${formData.notifications_enabled ? "text-[#196644]" : "text-gray-400"}`}>
                  {formData.notifications_enabled ? "On" : "Off"}
                </span>
              )}
            </div>

            <div>
              <Label className="text-sm text-[#0D382A] mb-3 block">Video Quality</Label>
              {isEditing ? (
                <RadioGroup
                  value={formData.video_quality}
                  onValueChange={(value) => setFormData({ ...formData, video_quality: value })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="high" id="quality-high" />
                    <Label htmlFor="quality-high" className="cursor-pointer">
                      High
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="medium" id="quality-medium" />
                    <Label htmlFor="quality-medium" className="cursor-pointer">
                      Medium
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="low" id="quality-low" />
                    <Label htmlFor="quality-low" className="cursor-pointer">
                      Low
                    </Label>
                  </div>
                </RadioGroup>
              ) : (
                <p className="text-[#0D382A] capitalize">{formData.video_quality || "High"}</p>
              )}
            </div>

            <div>
              <Label className="text-sm text-[#0D382A] mb-3 block">Dominant Hand</Label>
              {isEditing ? (
                <RadioGroup
                  value={formData.dominant_hand}
                  onValueChange={(value) => setFormData({ ...formData, dominant_hand: value })}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="right" id="hand-right" />
                    <Label htmlFor="hand-right" className="cursor-pointer">
                      Right
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="left" id="hand-left" />
                    <Label htmlFor="hand-left" className="cursor-pointer">
                      Left
                    </Label>
                  </div>
                </RadioGroup>
              ) : (
                <p className="text-[#0D382A] capitalize">{formData.dominant_hand || "Not set"}</p>
              )}
            </div>
          </div>
        </div>

        {/* Save/Cancel Buttons (only show when editing) */}
        {isEditing && (
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
              className="border-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-[#F9C94E] to-[#A7F3A1] text-[#0D382A] hover:shadow-lg"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        )}

        {/* Sign Out Section */}
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(13,56,42,0.08)] overflow-hidden">
          <div className="p-6">
            <Button
              variant="outline"
              onClick={handleSignOut}
              className="w-full border-gray-300 hover:bg-gray-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        {/* Delete Account Section */}
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(13,56,42,0.08)] overflow-hidden border-2 border-red-100">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-[#0D382A] text-xl mb-1">Delete Account</h3>
                <p className="text-sm text-gray-500">
                  Permanently delete your account and all associated data
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete your account? This action cannot be undone. All your
              data, including swing analyses, drills, and profile information, will be permanently
              deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteAccount}>
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BottomTabs />
    </div>
  );
}
